import { useState, useEffect } from 'react';
import { useCoachingStore } from '@/stores/useCoachingStore';
import { useIdentityStore } from '@/stores/useIdentityStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { X, MessageCircle, Send } from 'lucide-react';
import { coachingService } from '@/services/coachingService';

export function MicroCoach() {
  const { interventions, isCoachingEnabled, dismissIntervention, respondToIntervention, loadInterventions } = useCoachingStore();
  const { identities } = useIdentityStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [response, setResponse] = useState('');

  useEffect(() => {
    loadInterventions();
  }, []);

  // Pegar intervenção ativa mais recente
  const activeIntervention = interventions.find(i => !i.dismissed);

  // Auto-expandir se houver intervenção nova
  useEffect(() => {
    if (activeIntervention && !activeIntervention.userResponse) {
      setIsExpanded(true);
    }
  }, [activeIntervention]);

  if (!isCoachingEnabled || !activeIntervention) {
    return null;
  }

  const handleDismiss = async () => {
    if (activeIntervention) {
      await dismissIntervention(activeIntervention.id);
      setIsExpanded(false);
      setResponse('');
    }
  };

  const handleRespond = async () => {
    if (activeIntervention && response.trim()) {
      await respondToIntervention(activeIntervention.id, response);
      setResponse('');
      setIsExpanded(false);
    }
  };

  // Processar template do prompt
  const getPromptText = (): string => {
    const script = useCoachingStore.getState().scripts.find(
      s => s.scriptKey === activeIntervention.scriptTemplate
    );

    if (!script) return 'Como posso ajudar?';

    // Substituir placeholders
    const context = {
      identity: identities[0]?.identityStatement || 'sua melhor versão',
      habit: activeIntervention.context.habitId || '',
      ...activeIntervention.context
    };

    return coachingService.processPromptTemplate(script.promptText, context);
  };

  if (!isExpanded) {
    return (
      <div className="fixed bottom-20 right-4 z-50">
        <Button
          size="lg"
          className="rounded-full shadow-lg animate-pulse"
          onClick={() => setIsExpanded(true)}
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Coach
          {interventions.filter(i => !i.dismissed && !i.userResponse).length > 0 && (
            <span className="ml-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {interventions.filter(i => !i.dismissed && !i.userResponse).length}
            </span>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 right-4 z-50 animate-slide-up">
      <Card className="w-80 shadow-2xl">
        <div className="p-4 border-b flex items-center justify-between bg-primary text-primary-foreground">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <span className="font-medium">Micro-Coach</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="hover:bg-primary-foreground/20"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
          {/* Mensagem do coach */}
          <div className="bg-muted rounded-lg p-3">
            <p className="text-sm">{getPromptText()}</p>
          </div>

          {/* Resposta do usuário (se houver) */}
          {activeIntervention.userResponse && (
            <div className="bg-primary/10 rounded-lg p-3 ml-auto max-w-[80%]">
              <p className="text-sm">{activeIntervention.userResponse}</p>
            </div>
          )}

          {/* Campo de resposta */}
          {!activeIntervention.userResponse && (
            <div className="space-y-2">
              <Textarea
                placeholder="Digite sua resposta..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                rows={3}
                className="resize-none"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDismiss}
                >
                  Agora não
                </Button>
                <Button
                  size="sm"
                  onClick={handleRespond}
                  disabled={!response.trim()}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
